/**
 * Step 08 — Mental Health & Neurodiversity
 *
 * 14 sub-steps: identity, 4 condition categories (neurodevelopmental,
 * mental health, personality, neurological/chronic), age of realization,
 * sensory, cognitive, executive function, learning, communication,
 * support, masking, impact.
 *
 * All fields optional. Multi-selects store arrays of option-value strings.
 *
 * @module udp-schema/step-08
 */

import { z } from 'zod';

export const step08NeurodiversitySchema = z.object({
  /** Identity + diagnosis status. */
  neurodiverseIdentity: z
    .enum([
      'yes_diagnosed',
      'yes_self_identified',
      'exploring',
      'no_neurotypical',
      'prefer_not_to_say',
    ])
    .optional(),

  /** Neurodevelopmental conditions (core ND). */
  neurodevelopmentalConditions: z.array(z.string().trim()).optional(),

  /** Mental health & mood conditions. */
  mentalHealthConditions: z.array(z.string().trim()).optional(),

  /** Personality patterns (DSM-5 Cluster A/B/C). */
  personalityPatterns: z.array(z.string().trim()).optional(),

  /** Neurological & chronic conditions. */
  neurologicalChronicConditions: z.array(z.string().trim()).optional(),

  /** When the respondent realized they were neurodivergent. */
  ageOfRealization: z
    .enum([
      'childhood',
      'teen',
      'young_adult',
      'adult',
      'later_life',
      'still_exploring',
      'prefer_not_to_say',
    ])
    .optional(),

  /** Sensory profile (hyper/hypo per sense, seeking, overwhelm). */
  sensoryProfile: z.array(z.string().trim()).optional(),

  /** ND-associated cognitive strengths. */
  cognitiveStrengths: z.array(z.string().trim()).optional(),

  /** Executive function challenges. */
  executiveFunctionChallenges: z.array(z.string().trim()).optional(),

  /** Preferred learning style. */
  learningStyle: z
    .enum([
      'visual',
      'auditory',
      'kinesthetic',
      'reading_writing',
      'multimodal',
      'needs_repetition',
      'needs_structure',
      'prefers_autonomy',
      'prefer_not_to_say',
    ])
    .optional(),

  /** Preferred communication style. */
  communicationPreference: z
    .enum([
      'face_to_face',
      'written',
      'phone',
      'video',
      'small_groups',
      'one_on_one',
      'visual_aids',
      'async',
      'prefer_not_to_say',
    ])
    .optional(),

  /** Support & accommodations that help. */
  supportAndAccommodations: z.array(z.string().trim()).optional(),

  /** Masking frequency. */
  maskingAndBurnout: z
    .enum(['frequently', 'sometimes', 'rarely', 'never', 'not_sure', 'prefer_not_to_say'])
    .optional(),

  /** Impact on daily life. */
  impactOnLife: z
    .enum(['significant', 'moderate', 'mild', 'minimal', 'varies', 'prefer_not_to_say'])
    .optional(),
});

export type Step08NeurodiversityInput = z.infer<typeof step08NeurodiversitySchema>;
